import { createRoot } from "react-dom/client";
import { ConversationProvider } from "@elevenlabs/react";
import App from "./App.tsx";
import "./index.css";
import { CallMonitorProvider } from "./contexts/CallMonitorContext";
import { AuthProvider } from "./contexts/AuthContext";
import { TradeProvider } from "./contexts/TradeContext";
import { initializeSentry } from "./lib/sentry";
import "./lib/patchElevenLabsMalformedError";

const APP_SHELL_VERSION = __APP_VERSION__;
const APP_SHELL_VERSION_KEY = "roofgenius:app-shell-version";

const hasLegacyOfflineArtifacts = async () => {
  const hasServiceWorkers = "serviceWorker" in navigator
    ? (await navigator.serviceWorker.getRegistrations()).length > 0
    : false;

  const hasLegacyCaches = "caches" in window
    ? (await caches.keys()).some((cacheKey) => /workbox|precache|runtime|vite/i.test(cacheKey))
    : false;

  return hasServiceWorkers || hasLegacyCaches;
};

const clearStaleAppCaches = async () => {
  if ("serviceWorker" in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
  }

  if ("caches" in window) {
    const cacheKeys = await caches.keys();
    await Promise.all(cacheKeys.map((cacheKey) => caches.delete(cacheKey)));
  }
};

const ensureFreshAppShell = async () => {
  try {
    const currentVersion = window.localStorage.getItem(APP_SHELL_VERSION_KEY);
    const hasLegacyArtifacts = await hasLegacyOfflineArtifacts();

    if (currentVersion === APP_SHELL_VERSION && !hasLegacyArtifacts) {
      return true;
    }

    await clearStaleAppCaches();
    window.localStorage.setItem(APP_SHELL_VERSION_KEY, APP_SHELL_VERSION);
    window.location.reload();
    return false;
  } catch (error) {
    console.warn("Failed to refresh cached app shell:", error);
    return true;
  }
};

// Initialize error tracking
initializeSentry();

const bootstrap = async () => {
  const shouldRender = await ensureFreshAppShell();
  if (!shouldRender) return;

  // Global error handlers
  window.addEventListener('error', (event) => {
    console.error('Global error:', event.error);
  });

  window.addEventListener('unhandledrejection', (event) => {
    console.error('Unhandled promise rejection:', event.reason);
  });

  createRoot(document.getElementById("root")!).render(
    <AuthProvider>
      <TradeProvider>
        <ConversationProvider>
          <CallMonitorProvider>
            <App />
          </CallMonitorProvider>
        </ConversationProvider>
      </TradeProvider>
    </AuthProvider>
  );
};

void bootstrap();
